import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getApiBase } from '../../utils/apiBase';
import { formatActivityWhen } from '../../utils/archivePrivacy';

const API = getApiBase();

export function ArchiveActivityLog({ treeId, className, limit = 20 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    if (!treeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trees/${treeId}/activity`, { params: { limit } });
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [treeId, limit]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  if (loading) {
    return (
      <div className={cn('flex justify-center py-6', className)} data-testid="activity-log-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading activity" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <p className={cn('text-sm text-muted-foreground py-4', className)} data-testid="activity-log-empty">
        Collaboration activity will appear here — invites, privacy changes, exports, and overlap reviews.
      </p>
    );
  }

  return (
    <ul className={cn('space-y-2', className)} role="list" data-testid="archive-activity-log">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex gap-3 rounded-xl border border-[hsl(38_16%_88%)]/80 bg-white/60 px-3 py-2.5 text-sm"
        >
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <p className="text-[hsl(24_14%_16%)]">{item.summary}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {item.actor_name} · {formatActivityWhen(item.created_at)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default ArchiveActivityLog;
